import { ChangeDetectionStrategy, Component, effect, input, model, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { SiFileDropzoneComponent, UploadFile } from '@siemens/element-ng/file-uploader';
import { SiLoadingSpinnerComponent } from '@siemens/element-ng/loading-spinner';
import { File } from '@tran-monorepo/interfaces/files';
import { FileListComponent, UploadFileItemComponent } from '../../files';
import { ApplicationDetailDashboardBoxComponent } from '../application-detail-dashboard-box/application-detail-dashboard-box.component';
import { TRAN_DETAIL_UPLOADER_CONFIG } from './application-attachments-upload.config';

@Component({
    selector: 'tran-application-attachments',
    imports: [
        ApplicationDetailDashboardBoxComponent,
        SiFileDropzoneComponent,
        SiLoadingSpinnerComponent,
        FileListComponent,
        TranslatePipe,
        UploadFileItemComponent
    ],
    templateUrl: './application-attachments.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplicationAttachmentsComponent {
    public readonly heading = input<string>(
        'components.application-detail-dashboard.application-detail-attachments.heading'
    );
    public readonly noAttachmentsDisclaimer = input<string>(
        'components.application-detail-dashboard.application-detail-attachments.no-attachments'
    );
    public readonly isLoading = model<boolean>(false);
    public readonly attachments = input<File[]>([]);
    public readonly hasMoreAttachments = input<boolean>(false);
    public readonly readonly = input<boolean>(true);
    public readonly loadMore = output<void>();
    public readonly download = output<File>();
    public readonly fileUploadAdded = output<UploadFile[]>();
    public readonly filesDeleted = output<File[]>();

    protected readonly selectedFiles = signal<UploadFile[]>([]);
    private readonly deletedFiles = signal<File[]>([]);

    protected readonly fileUploaderConfig = TRAN_DETAIL_UPLOADER_CONFIG;

    public constructor() {
        effect(() => {
            const isReadonly = this.readonly();
            if (!isReadonly) {
                this.clearAllAddedFiles();
            }
        });
    }

    protected onFilesAdded(files: UploadFile[]): void {
        const validFiles = files.filter(f => f.status !== 'invalid');
        const currentFiles = this.selectedFiles();
        const updatedFiles = [...currentFiles, ...validFiles];

        this.selectedFiles.set(updatedFiles);
        this.fileUploadAdded.emit(updatedFiles);
    }

    protected removeAddedFile(fileToRemove: UploadFile): void {
        const updatedFiles = this.selectedFiles().filter(f => f.file !== fileToRemove.file);
        this.selectedFiles.set(updatedFiles);
        this.fileUploadAdded.emit(updatedFiles);
    }

    protected clearAllAddedFiles(): void {
        this.selectedFiles.set([]);
        this.fileUploadAdded.emit([]);
    }

    protected addFileToDelete(file: File): void {
        this.deletedFiles.update((files: File[]) => [...files, file]);
        this.filesDeleted.emit(this.deletedFiles());
    }

    protected removeFileFromDelete(fileToRemove: File): void {
        const updatedFiles = this.deletedFiles().filter(f => f.id !== fileToRemove.id);
        this.deletedFiles.set(updatedFiles);
        this.filesDeleted.emit(updatedFiles);
    }
}
